import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { MapGroupLayer } from '../../map/map-layer-manager/map-layer.enum';
import type { HistoricVesselPoint } from '../../maritime-menu-options/history-panel/historic-vessel-point.model';
import type { VesselHistoryData } from '../../models/vessels/maritime-ais-api';
import type { Vessel } from '../../models/vessels/vessel.model';

export interface MergedHistoricVesselPoint
  extends Pick<Vessel, 'mmsi' | 'imo'> {
  name: string;
  vesselId: string;
  features: GeoJSON.Feature<GeoJSON.Point, GeoJSON.GeoJsonProperties>[];
  startTimestamp: number;
  endTimestamp: number;
  index: number;
  durationInMinutes: number;
}

export interface MergedPointsFilters {
  minimumCount: number;
  minimumGapLengthHours: number;
  maximumDistanceMetres: number;
}

export interface DraughtChangesFilters {
  minimumChangeMetres: number;
}

type IHistoryState = {
  disabled: boolean;
  selectedAISDataGapFeature: GeoJSON.Feature | null;
  selectedMergedPointIndex: number | null;
  featureVesselFilters: VisibilityState;
  historicVesselPoints: HistoricVesselPoint[];
  filteredVesselIds: string[];
  vesselHistoryData: VesselHistoryData | null;
  mergedPointsFilters: MergedPointsFilters;
  draughtChangesFilters: DraughtChangesFilters;
};

type VisibilityState = {
  [MapGroupLayer.VESSEL_HISTORY_MERGED_POINTS]: string[];
};
const defaultVisiblityState: VisibilityState = {
  [MapGroupLayer.VESSEL_HISTORY_MERGED_POINTS]: [],
};

export const INITIAL_HISTORY_STATE: IHistoryState = {
  disabled: false,
  selectedAISDataGapFeature: null,
  selectedMergedPointIndex: null,
  featureVesselFilters: defaultVisiblityState,
  historicVesselPoints: [],
  filteredVesselIds: [],
  vesselHistoryData: null,
  mergedPointsFilters: {
    minimumCount: 10,
    minimumGapLengthHours: 12,
    maximumDistanceMetres: 100,
  },
  draughtChangesFilters: {
    minimumChangeMetres: 0,
  },
};

const HistorySlice = createSlice({
  name: 'history',
  initialState: INITIAL_HISTORY_STATE,
  reducers: {
    setSelectedAISDataGapFeature: (
      state,
      action: PayloadAction<GeoJSON.Feature | null>
    ) => {
      state.selectedAISDataGapFeature = action.payload;
    },
    setHistoricVesselPoints: (
      state,
      action: PayloadAction<HistoricVesselPoint[]>
    ) => {
      state.historicVesselPoints = action.payload;
    },
    prependHistoricVesselPoints: (
      state,
      action: PayloadAction<HistoricVesselPoint[]>
    ) => {
      state.historicVesselPoints = [
        ...action.payload,
        ...state.historicVesselPoints,
      ];
    },
    setVesselHistoryData: (
      state,
      action: PayloadAction<VesselHistoryData | null>
    ) => {
      state.vesselHistoryData = action.payload;
    },
    clearVesselHistoryData: (state) => {
      state.selectedAISDataGapFeature = null;
      state.selectedMergedPointIndex = null;
      state.vesselHistoryData = null;
      state.historicVesselPoints = [];
    },
    setFilteredVesselIds: (state, action: PayloadAction<string[]>) => {
      state.filteredVesselIds = action.payload;
    },
    setMergedPointsFilters(
      state,
      action: PayloadAction<Partial<MergedPointsFilters>>
    ) {
      state.mergedPointsFilters = {
        ...state.mergedPointsFilters,
        ...action.payload,
      };
    },
    setDraughtChangesFilters(
      state,
      action: PayloadAction<Partial<DraughtChangesFilters>>
    ) {
      state.draughtChangesFilters = {
        ...state.draughtChangesFilters,
        ...action.payload,
      };
    },
  },
});

export const {
  setSelectedAISDataGapFeature,
  setHistoricVesselPoints,
  prependHistoricVesselPoints,
  setVesselHistoryData,
  clearVesselHistoryData,
  setFilteredVesselIds,
  setMergedPointsFilters,
  setDraughtChangesFilters,
} = HistorySlice.actions;

export default HistorySlice.reducer;
